import React, { useState, useEffect, useRef } from 'react';
import { Ticket } from '../../../entities/ticket';
import { GetTicketsUseCase } from '../application/getTicketsUseCase';
import * as S from './TicketList.styles';

interface TicketListProps {
  selectedTicketId?: string;
  onTicketSelect: (ticket: Ticket) => void;
  onCreateTicket: () => void;
  refreshTrigger?: number;
  onVisibleTicketsChange?: (tickets: Ticket[]) => void;
}

const QUEUE_STATUSES = ['new', 'active', 'waiting_info'] as const;
const PAGE_SIZE = 250;
const POLL_INTERVAL_MS = 1000;

const isQueueTicket = (ticket: Ticket): boolean => {
  if (ticket.status === 'closed' || ticket.status === 'resolved' || ticket.status === 'spam' || ticket.status === 'duplicate' || ticket.status === 'in_programming') {
    return false;
  }

  const lastMessage = ticket.lastMessage;
  if (lastMessage && lastMessage.direction === 'incoming' && !lastMessage.isInternal) {
    return true;
  }

  return ticket.status === 'new' || ticket.status === 'active' || ticket.status === 'waiting_info';
};

const sortTicketsOldestFirst = (tickets: Ticket[]): Ticket[] =>
  tickets.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

const normalizeQueueTickets = (tickets: Ticket[]): Ticket[] =>
  sortTicketsOldestFirst(tickets.filter(isQueueTicket));

const mergeTicketsById = (current: Ticket[], updates: Ticket[]): Ticket[] => {
  const byId = new Map<string, Ticket>(current.map(ticket => [ticket.id, ticket]));
  for (const updatedTicket of updates) {
    byId.set(updatedTicket.id, updatedTicket);
  }
  return Array.from(byId.values());
};

export const TicketList: React.FC<TicketListProps> = ({
  selectedTicketId,
  onTicketSelect,
  onCreateTicket,
  refreshTrigger,
  onVisibleTicketsChange,
}) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const requestSeqRef = useRef(0);
  const isSyncingRef = useRef(false);
  const lastSyncAtRef = useRef<string | null>(null);
  const [filters, setFilters] = useState<{
    site: string;
  }>({
    site: '',
  });

  const getTicketsUseCase = new GetTicketsUseCase();

  const applyTickets = (nextTickets: Ticket[] | ((prevTickets: Ticket[]) => Ticket[])) => {
    setTickets(prevTickets => {
      const computedTickets = typeof nextTickets === 'function'
        ? nextTickets(prevTickets)
        : nextTickets;
      onVisibleTicketsChange?.(computedTickets);
      return computedTickets;
    });
  };

  const getSiteFilter = () => {
    if (filters.site && filters.site.trim() !== '') {
      return filters.site;
    }
    return undefined;
  };

  const loadRemainingPagesInBackground = async (
    requestSeq: number,
    site: string | undefined,
  ) => {
    let offset = PAGE_SIZE;

    while (requestSeq === requestSeqRef.current) {
      const page = await getTicketsUseCase.execute({
        site,
        status: [...QUEUE_STATUSES],
        limit: PAGE_SIZE,
        offset,
        orderBy: 'created_asc',
      });

      if (requestSeq !== requestSeqRef.current || page.length === 0) {
        break;
      }

      applyTickets(prevTickets => normalizeQueueTickets(mergeTicketsById(prevTickets, page)));

      if (page.length < PAGE_SIZE) {
        break;
      }

      offset += PAGE_SIZE;
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  };

  const loadTickets = async ({ silent = false }: { silent?: boolean } = {}) => {
    const requestSeq = ++requestSeqRef.current;
    const site = getSiteFilter();

    try {
      if (!hasLoadedOnce && !silent) {
        setLoading(true);
      }

      const firstPage = await getTicketsUseCase.execute({
        site,
        status: [...QUEUE_STATUSES],
        limit: PAGE_SIZE,
        offset: 0,
        orderBy: 'created_asc',
      });

      if (requestSeq !== requestSeqRef.current) {
        return;
      }

      applyTickets(normalizeQueueTickets(firstPage));
      lastSyncAtRef.current = new Date().toISOString();
      if (!hasLoadedOnce) {
        setHasLoadedOnce(true);
      }

      if (firstPage.length >= PAGE_SIZE) {
        void loadRemainingPagesInBackground(requestSeq, site);
      }
    } catch (error) {
      console.error('Ошибка загрузки тикетов:', error);
    } finally {
      if (!hasLoadedOnce) {
        setLoading(false);
      }
    }
  };

  const syncTicketChanges = async () => {
    if (document.hidden || isSyncingRef.current || !hasLoadedOnce) {
      return;
    }
    const updatedAfter = lastSyncAtRef.current;
    if (!updatedAfter) {
      lastSyncAtRef.current = new Date().toISOString();
      return;
    }

    isSyncingRef.current = true;
    const nowIso = new Date().toISOString();
    const site = getSiteFilter();

    try {
      const updatedTickets = await getTicketsUseCase.execute({
        site,
        updatedAfter,
        limit: PAGE_SIZE,
        orderBy: 'updated_desc',
      });

      lastSyncAtRef.current = nowIso;

      if (updatedTickets.length === 0) {
        return;
      }

      applyTickets(prevTickets => normalizeQueueTickets(mergeTicketsById(prevTickets, updatedTickets)));

      if (updatedTickets.length >= PAGE_SIZE) {
        void loadTickets({ silent: true });
      }
    } catch (error) {
      console.error('Ошибка синхронизации тикетов:', error);
    } finally {
      isSyncingRef.current = false;
    }
  };

  useEffect(() => {
    void loadTickets({ silent: hasLoadedOnce });
  }, [filters.site, refreshTrigger]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void syncTicketChanges();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [filters.site, hasLoadedOnce]);


  const getLastMessagePreview = (ticket: Ticket): string => {
    if (ticket.lastMessage) {
      return ticket.lastMessage.content;
    }
    return '';
  };

  return (
    <S.TicketListContainer>
      <S.TicketListHeader>
        <S.CreateTicketButton onClick={onCreateTicket}>
          + Создать тикет
        </S.CreateTicketButton>
      </S.TicketListHeader>

      <S.FiltersContainer>
        <S.SiteFilter>
          <S.SiteFilterLabel>Сайт:</S.SiteFilterLabel>
          <S.SiteFilterSelect
            value={filters.site}
            onChange={(e) => setFilters(prev => ({ ...prev, site: e.target.value }))}
          >
            <option value="">Все</option>
            <option value="hudle">Hudle</option>
            <option value="tournament">Турнирный сайт</option>
          </S.SiteFilterSelect>
        </S.SiteFilter>
      </S.FiltersContainer>

      <S.TicketsSection>
        <S.TicketsSectionTitle>ТИКЕТЫ</S.TicketsSectionTitle>
        {loading ? (
          <S.LoadingState>
            Загрузка...
          </S.LoadingState>
        ) : tickets.length === 0 ? null : (
          tickets.map(ticket => (
            <S.TicketItem
              key={ticket.id}
              $active={selectedTicketId === ticket.id}
              $hasUnread={ticket.hasUnreadMessages}
              onClick={() => onTicketSelect(ticket)}
            >
              <S.TicketItemHeader>
                <S.TicketItemTitle>
                  {ticket.hasUnreadMessages && <S.UnreadIndicator />}
                  <S.TicketItemTitleText>{ticket.title}</S.TicketItemTitleText>
                  <S.TicketSiteBadge $site={ticket.site}>
                    {ticket.site === 'hudle' ? 'Hudle' : ticket.site === 'unknown' ? 'Неизвестный' : 'Турнир'}
                  </S.TicketSiteBadge>
                </S.TicketItemTitle>
              </S.TicketItemHeader>
              <S.TicketItemPreview>
                {getLastMessagePreview(ticket)}
              </S.TicketItemPreview>
            </S.TicketItem>
          ))
        )}
      </S.TicketsSection>
    </S.TicketListContainer>
  );
};
